import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import type { MapConfig, Region } from '../../api/types'
import { kacheloptionen, plainLabel } from '../../lib/kartenquelle'

/** Close enough to see a town and its surroundings -- the same as the book's cut-outs. */
const ZOOM = 11

/**
 * A small map to pick the centre of a protected region (#99, 1.54.0). From
 * use: "idealerweise aus einer Karte wählbar". A tap sets the centre, the
 * circle shows the radius; the regions that exist stand as circles too.
 *
 * The tiles come from the same source as the trip's map (the first
 * exception in CLAUDE.md), and this map is seen by the administration's
 * browser alone -- the settings are theirs. No name search: that would be
 * a third free search field at Apple, and the two that exist are counted
 * where they stand. Whoever cannot find home by dragging types the two
 * numbers next to it, or takes "Hier".
 */
export function Regionskarte({
  quelle,
  regionen,
  mitte,
  radius,
  onWahl,
}: {
  quelle: MapConfig
  regionen: Region[]
  /** The draft's centre -- none until tapped, typed or located. */
  mitte: { lat: number; lon: number } | null
  radius: number
  onWahl: (lat: number, lon: number) => void
}) {
  const kasten = useRef<HTMLDivElement>(null)
  const karte = useRef<L.Map | null>(null)
  const ebene = useRef<L.LayerGroup | null>(null)
  const wahl = useRef(onWahl)
  wahl.current = onWahl
  // Steht hier oben, weil der Aufbau ihn beim Abräumen zurücksetzt.
  const gesprungen = useRef<string | null>(null)

  useEffect(() => {
    if (kasten.current === null || karte.current !== null) return
    const map = L.map(kasten.current, { zoomControl: true, attributionControl: true })
    L.tileLayer(quelle.tile_url, kacheloptionen(quelle)).addTo(map)
    map.setView([20, 0], 2)
    map.on('click', (event: L.LeafletMouseEvent) => wahl.current(event.latlng.lat, event.latlng.lng))
    ebene.current = L.layerGroup().addTo(map)
    karte.current = map
    return () => {
      map.remove()
      karte.current = null
      ebene.current = null
      // Nach einem Neubau ist auch der Sprung wieder offen: die Karte steht
      // dann auf [20, 0] bei Zoom 2, und ein Merkzettel von vorher hielte sie
      // dort fest.
      gesprungen.current = null
    }
  }, [quelle])

  // The circles: the regions that exist, and the draft on top.
  useEffect(() => {
    const map = karte.current
    const gruppe = ebene.current
    if (map === null || gruppe === null) return
    gruppe.clearLayers()
    for (const region of regionen) {
      L.circle([region.lat, region.lon], { radius: region.radius_m, color: '#8e8e93', weight: 1, fillOpacity: 0.12 })
        .bindTooltip(plainLabel(region.name))
        .addTo(gruppe)
    }
    if (mitte !== null) {
      L.circle([mitte.lat, mitte.lon], { radius, color: '#007aff', weight: 2, fillOpacity: 0.18 }).addTo(gruppe)
      L.circleMarker([mitte.lat, mitte.lon], { radius: 5, color: '#007aff', fillOpacity: 1 }).addTo(gruppe)
    }
    /*
     * **`quelle` gehört dazu, obwohl hier keine Kachel vorkommt** (1.66.0, aus
     * der Codereview).
     *
     * Wechselt die Kartenquelle, baut der Effekt darüber die Karte neu -- samt
     * einer **frischen, leeren** Ebene. Diese Zusammenstellung lief dann nicht
     * noch einmal, weil sich an `regionen`, `mitte` und `radius` nichts
     * geändert hatte: die Kreise waren weg und kamen erst wieder, wenn jemand
     * einen Radius anfasste. Was an einer Ebene hängt, hängt an dem, was die
     * Ebene neu baut.
     */
  }, [quelle, regionen, mitte, radius])

  // The view follows the draft once (a typed or located centre), not every
  // tap: a tap already lies in the picture.
  useEffect(() => {
    const map = karte.current
    if (map === null || mitte === null) return
    const kennung = `${mitte.lat.toFixed(4)},${mitte.lon.toFixed(4)}`
    if (gesprungen.current === kennung) return
    gesprungen.current = kennung
    if (!map.getBounds().contains([mitte.lat, mitte.lon]) || map.getZoom() < 6) {
      map.setView([mitte.lat, mitte.lon], Math.min(ZOOM, quelle.max_zoom))
    }
  }, [mitte, quelle.max_zoom])

  return <div className="regionskarte" ref={kasten} data-regionskarte="" />
}
